import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getUnreadCount } from "../services/notificationService";

// ─────────────────────────────────────────────────────────────────────────────
//  NotificationContext
//
//  Fournit le compteur de notifications non lues à toute l'application,
//  mis à jour en temps réel via SSE (Server-Sent Events) dès qu'une
//  notification est créée côté backend.
//
//  Le polling toutes les 2 min reste en place comme filet de sécurité —
//  si la connexion SSE tombe (redémarrage backend, coupure réseau...),
//  le compteur reste quand même à jour, juste avec un peu de retard.
// ─────────────────────────────────────────────────────────────────────────────

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {

  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    fetchCount();

    // ── Connexion SSE — pousse les notifs en temps réel ──────────────────
    const token = localStorage.getItem("token");
    if (token) {
      // Le token passe en query param car l'API native EventSource ne
      // permet pas d'envoyer de header Authorization personnalisé.
      const source = new EventSource(`/api/notifications/stream?token=${encodeURIComponent(token)}`);
      eventSourceRef.current = source;

      source.addEventListener("notification", () => {
        // Une notification vient d'être créée pour cet utilisateur →
        // on incrémente immédiatement, sans attendre le prochain polling.
        setUnreadCount(prev => prev + 1);
      });

      // Pas de logique de reconnexion à écrire : EventSource s'en charge
      // nativement (retente automatiquement toutes les quelques secondes).
      source.onerror = () => {
        // On ne fait rien de spécial ici — le polling de secours ci-dessous
        // continue de tourner en parallèle pendant que le navigateur
        // retente la connexion SSE tout seul.
      };
    }

    // ── Polling de secours toutes les 2 min ──────────────────────────────
    const interval = setInterval(fetchCount, 120_000);

    return () => {
      clearInterval(interval);
      eventSourceRef.current?.close();
    };
  }, []);

  async function fetchCount() {
    try {
      const res = await getUnreadCount();
      setUnreadCount(res.data ?? 0); // ApiResponse → .data contient le nombre
    } catch {
      // Silencieux : on ne bloque pas l'UI si l'appel échoue
    }
  }

  // ── decrementUnread : appelé depuis NotificationsPage après markAsRead ──
  function decrementUnread(by = 1) {
    setUnreadCount(prev => Math.max(0, prev - by));
  }

  // ── resetUnread : appelé depuis NotificationsPage après markAllAsRead ──
  function resetUnread() {
    setUnreadCount(0);
  }

  return (
    <NotificationContext.Provider value={{ unreadCount, decrementUnread, resetUnread }}>
      {children}
    </NotificationContext.Provider>
  );
}

// Hook utilitaire — usage : const { unreadCount } = useNotifications();
export function useNotifications() {
  return useContext(NotificationContext);
}